import { useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setSessionExpiredHandler } from "@/api/claimsInvoicingApi";
import { showSessionExpiredToast } from "@/auth/sessionExpired";

const useSessionExpiration = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const hasHandledRef = useRef(false);

    // Track authentication across refresh
    useEffect(() => {
        if (auth.isAuthenticated) {
            sessionStorage.setItem("wasAuthenticated", "true");
            hasHandledRef.current = false;
        }
    }, [auth.isAuthenticated]);

    // Detect session expiration
    useEffect(() => {
        const shouldSkip =
            auth.isLoading || location.pathname === "/signout" || auth.activeNavigator === "signinSilent";

        if (shouldSkip) {
            return;
        }

        const wasAuthenticated = sessionStorage.getItem("wasAuthenticated") === "true";

        if (!auth.isAuthenticated && wasAuthenticated && !hasHandledRef.current) {
            hasHandledRef.current = true;

            showSessionExpiredToast(t);
            sessionStorage.removeItem("wasAuthenticated");

            navigate("/", { replace: true });
        }
    }, [auth.isAuthenticated, auth.isLoading, auth.activeNavigator, location.pathname, navigate, t]);

    // Handle API 401
    useEffect(() => {
        setSessionExpiredHandler(() => {
            if (hasHandledRef.current) {
                return;
            }

            hasHandledRef.current = true;

            showSessionExpiredToast(t);
            sessionStorage.removeItem("wasAuthenticated");

            auth.signoutRedirect({
                redirectMethod: "replace",
                // eslint-disable-next-line camelcase
                post_logout_redirect_uri: import.meta.env.BASE_URL + "/"
            });
        });
    }, [auth, t]);
};

export { useSessionExpiration };
